---
title: "Breakout vs Mean Reversion Strategies"
slug: breakout-vs-mean-reversion
shortTitle: "Breakout vs Mean Reversion"
description: "Two opposite ways to trade the same chart: when breakouts tend to work, when mean reversion does, where the stops go, and how to tell which kind of market you are in."
level: advanced
category: trading
course: advanced
order: 4
topics: [trading-strategies]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/breakout-vs-mean-reversion.svg
faqs:
  - q: "Is breakout trading better than mean reversion?"
    a: "Neither is better in general. Breakouts tend to do well when a market starts to trend and poorly in choppy ranges, and mean reversion is the opposite. What matters is matching the approach to the conditions and testing it on your own data."
  - q: "What is a false breakout?"
    a: "A false breakout is a move through support or resistance that quickly reverses back into the range. It is the most common way breakout trades lose, which is why breakout traders usually keep the stop back inside the range."
  - q: "Why does a mean-reversion strategy need a stop loss if it wins often?"
    a: "Because its losing trades can be much larger than its winners. A range that turns into a trend can keep moving against you, and without a stop one trade can wipe out many small gains."
  - q: "Which order types suit each strategy on {{BRAND}}?"
    a: "Breakout entries are often stop orders placed beyond the level, such as a Buy Stop above resistance. Mean-reversion entries are usually limit orders near the edge of a range, such as a Sell Limit near resistance. Both are set on the Limit / Stop tab of the order panel."
  - q: "Can I use both strategies?"
    a: "Some traders use breakouts in trending conditions and mean reversion in ranges, with written rules for telling the two apart. It only helps if those rules are fixed in advance and tested. Switching approach in the middle of a trade usually turns a plan into a guess."
---

Look at any chart that has gone sideways for a while and you can make two opposite cases. One trader says the price is coiling and will burst out of the range: a **breakout** idea. Another says every push to the edge gets sold or bought back: a **mean-reversion** idea. Both can be right, just at different times.

This lesson compares the two: how each trade is built, where the stop and target go, what results tend to look like over many trades, and how to judge which one fits the market in front of you. Every number is illustrative, and nothing here is a signal to trade.

<CTA />

<KeyTakeaways>

- **They bet on opposite things.** A breakout trade bets that a move through a level continues; a mean-reversion trade bets that a stretched price snaps back toward its usual level.
- **The market's regime decides which works.** Breakouts need trends to follow; mean reversion needs ranges that hold. Each strategy's best conditions are the other's worst.
- **Their results look different.** Breakouts usually win less often but win bigger; mean reversion usually wins more often, with smaller gains and the risk of an occasional large loss.
- **The stop defines the idea.** A breakout is wrong if the price falls back into the range; a reversion trade is wrong if the range breaks. Put the stop where the idea fails.

</KeyTakeaways>

## Two opposite ideas about the same move

A **range** is a stretch of time when the price moves sideways between a floor (support) and a ceiling (resistance). The two strategies take opposite views of what happens at its edges.

- **Breakout trading** assumes that when the price pushes through support or resistance, the orders waiting beyond the level and the traders caught on the wrong side will fuel a further move. Quiet, tightening ranges are often followed by bigger moves.
- **Mean reversion** assumes that a price stretched far from its average, or pressed against the edge of an established range, tends to drift back toward the middle. The "mean" can be a moving average, the midpoint of a range or a reading on an indicator such as RSI.

Neither idea is a law of nature; both are tendencies that come and go. Both also depend on drawing [support and resistance](/academy/article/support-and-resistance) honestly.

## How a breakout trade is built

A breakout trader defines the break in advance, enters when it happens and puts the stop back inside the range. A common target is the **measured move**: the range's height added to the breakout point.

On {{BRAND}} you can place the entry before the break. On the **Limit / Stop** tab, a buy price above the Ask becomes a **Buy Stop**, and a sell price below the Bid becomes a **Sell Stop**. There are no stop-limit orders, so a triggered stop fills at market, which in a fast break can be worse than your level.

<Example title="A BTC/USDT range breakout (illustrative prices)">

BTC/USDT has traded between 58,000 and 60,000 USDT for a week. You place a **Buy Stop** at 60,200 for 0.05 BTC.

- Stop loss back inside the range at 59,400: risk = (60,200 − 59,400) × 0.05 = **40 USDT**.
- Measured-move target: range height 2,000 added to 60,000 = 62,000. Reward = (62,000 − 60,200) × 0.05 = **90 USDT**.
- Reward-to-risk = 90 ÷ 40 = **2.25 to 1**.
- Position value = 0.05 × 60,200 = 3,010 USDT. At an example leverage of 5x, the margin is 3,010 ÷ 5 = 602 USDT.

A fill at 60,260 instead would raise the risk to 860 × 0.05 = 43 USDT.

</Example>

The main risk is the **false breakout**: the price pokes through, triggers the orders above the level, then falls back into the range. Breakout traders accept many small failures and rely on the few that run. Common filters: wait for a candle to *close* beyond the level, check that volume rose on the break (the chart's volume pane shows it), and favor breaks in the direction of the higher-timeframe trend.

## How a mean-reversion trade is built

A mean-reversion trader sells near resistance and buys near support, or fades a price stretched unusually far from its average. The entry is usually a **limit order** at the edge: a sell price above the Bid becomes a **Sell Limit**, a buy price below the Ask a **Buy Limit**. The stop goes just beyond the range, because a clean break means the idea is gone.

<Example title="A gold range fade (illustrative prices)">

GOLD/USDT has held between 2,360 and 2,400 for two weeks. You place a **Sell Limit** at 2,396 for 0.02 lots. With 1 lot of gold being 100 oz, that is 2 oz.

- Stop loss above the range at 2,408: risk = (2,408 − 2,396) × 2 = **24 USDT**.
- Target at the middle of the range, 2,380: reward = (2,396 − 2,380) × 2 = **32 USDT**, about 1.3 to 1.
- Position value = 2,396 × 2 = 4,792 USDT.

If the range holds, trades like this can win often. If gold breaks out, the stop is what keeps the loss at a planned 24 USDT.

</Example>

<Warning title="Fading a strong move is not the same as buying cheap">

Mean reversion goes wrong when a range turns into a trend. Adding to a losing fade ("averaging down") or removing the stop because the price "must come back" turns a small, planned loss into a large one, and leverage magnifies it. Most traders who trade CFDs lose money. Keep the stop, and size so that hitting it is a loss you can accept.

</Warning>

## Side by side

<CompareTable caption="Breakout and mean-reversion trades compared">

| | Breakout | Mean reversion |
| --- | --- | --- |
| Market it needs | A range ending and a trend starting | A range or stretched move that holds |
| Typical entry | Buy Stop above resistance, Sell Stop below support | Sell Limit near resistance, Buy Limit near support |
| Stop goes | Back inside the range | Just beyond the edge of the range |
| Typical target | Measured move or trailing exit | Middle or opposite side of the range |
| Win rate profile | Lower, with larger winners | Higher, with smaller winners |
| Main way it fails | False breakouts in choppy markets | A range that breaks and trends |
| Hardest part | Sitting through many small losses | Accepting the stop when the range breaks |

</CompareTable>

A breakout trader can lose more than half the time and still come out ahead if the winners are large enough; a mean-reversion trader can win most of the time and still lose money if the occasional loss is big. [Risk/reward and win rate](/academy/article/risk-reward-and-win-rate) works through that math.

## Reading the regime before you pick a side

The single most useful question is: *is this market ranging or trending right now, on the timeframe I trade?*

- **Ranging clues.** Repeated turns at similar highs and lows, a flat moving average that price keeps crossing, steady or shrinking volatility.
- **Trending clues.** Higher highs and higher lows (or the reverse), price holding one side of its moving average, shallow pullbacks.
- **Timeframe conflicts.** A clean range on the 1h chart can be a pause inside a strong 1D trend. [Multi-timeframe analysis](/academy/article/multi-timeframe-analysis) helps you see which picture dominates.
- **Scheduled events.** A data release or earnings report can end a range in seconds; check the economic calendar on the **Markets** page.

### Using the opportunity scanner as a starting point

The **Opportunity scanner** on the [Markets](/markets) page has screens that map onto these two ideas. **Breakouts** and **Breakdowns** list markets above their previous 24-hour high or below their previous 24-hour low. **Overbought** and **Oversold** list markets whose hourly RSI(14) is at or above 70, or at or below 30, a classic mean-reversion starting point. As the scanner says, these are screens, not advice: they describe what has happened, not what comes next.

The trade chart has no indicators or drawing tools, so if your rules use trendlines or bands, you can draw them in a separate charting tool and place the orders on {{BRAND}}.

{/* TODO: verify with platform — scanner screen names and thresholds, and that the trade chart still has no indicators or drawing tools. */}

## A pre-trade checklist for either approach

<Steps>
<Step title="Name the regime">

Write "range" or "trend" for your timeframe and the one above it. If you cannot tell, wait.

</Step>
<Step title="Mark the level that proves you wrong">

For a breakout, back inside the broken level; for a fade, just beyond the range edge. That is your SL Price.

</Step>
<Step title="Pick the matching order">

Stop orders for breakouts, limit orders for fades, both on the **Limit / Stop** tab. Pending orders reserve margin as soon as they are placed.

</Step>
<Step title="Size from the stop and read the summary">

Switch on **TP / SL**, check the **Loss (USDT)** figure, and adjust the size until it fits your plan. Then read **Margin required**, **Fee** and **Value of $1 move** before you confirm.

</Step>
</Steps>

<ProTip title="Do not switch strategies mid-trade">

A failed breakout is not a mean-reversion trade, and a broken range is not a breakout you meant to take. If the price hits your stop, take the planned exit and reassess with a flat position.

</ProTip>

## Conclusion

Breakout and mean-reversion strategies are mirror images: one bets that a move through a level continues, the other that a stretched price returns. Each works in the conditions the other dislikes, so the skill lies in recognizing the regime, using the order type that fits it, and putting the stop where the idea fails. Test any version on past charts and on a demo account first, keep the risk per trade small, and expect long stretches when the market does not suit your approach.
